import React, { useState } from 'react';
import {
  Truck, MapPin, Phone, Package, User, CheckCircle,
  Clock, XCircle, RefreshCw, Filter, ChevronDown,
  Bike, AlertCircle, TrendingUp, Star
} from 'lucide-react';

// ─── Mock Data ────────────────────────────────────────────────────────────────
const MOCK_LIVREURS = [
  { id: 'L1', nom: 'Ibrahima Diallo',  tel: '+221 77 111 22 33', dispo: true  },
  { id: 'L2', nom: 'Moussa Sarr',       tel: '+221 76 444 55 66', dispo: true  },
  { id: 'L3', nom: 'Ousmane Ndiaye',    tel: '+221 78 777 88 99', dispo: false },
];

const MOCK_COMMANDES_INIT = [
  {
    id: 'CMD-001', patient: 'Mamadou Diallo', tel: '+221 77 123 45 67',
    medicament: 'Paracétamol 1g × 2 boîtes', adresse: 'Darou Khoudoss, près de la mosquée',
    commune: 'Touba', montant: 3200, paiement: 'Wave',
    statut: 'EN_ATTENTE', livreurId: null, heure: '10:32',
    note: 'Appeler avant de passer',
  },
  {
    id: 'CMD-002', patient: 'Fatou Mbaye', tel: '+221 76 987 65 43',
    medicament: 'Amoxicilline 500mg + Doliprane sirop', adresse: 'Keur Niang, maison bleue',
    commune: 'Touba', montant: 5800, paiement: 'OM',
    statut: 'EN_ATTENTE', livreurId: null, heure: '10:47',
    note: '',
  },
  {
    id: 'CMD-003', patient: 'Serigne Sall', tel: '+221 78 321 65 44',
    medicament: 'Metronidazole 500mg', adresse: 'Ndamatou, quartier 7',
    commune: 'Touba', montant: 2100, paiement: 'Cash',
    statut: 'EN_COURS', livreurId: 'L1', heure: '09:15',
    note: '',
  },
  {
    id: 'CMD-004', patient: 'Aissatou Ba', tel: '+221 77 654 32 10',
    medicament: 'Chloroquine 100mg', adresse: 'Guédé, près école', commune: 'Touba',
    montant: 1500, paiement: 'Wave',
    statut: 'LIVREE', livreurId: 'L2', heure: '08:05',
    note: 'Livré à 09:22',
  },
  {
    id: 'CMD-005', patient: 'Omar Faye', tel: '+221 76 112 23 34',
    medicament: 'Sérum physiologique × 5', adresse: 'Mbacké Baol, secteur 3',
    commune: 'Mbacké', montant: 4000, paiement: 'OM',
    statut: 'EN_ATTENTE', livreurId: null, heure: '11:03',
    note: 'Urgence — enfant malade',
  },
  {
    id: 'CMD-006', patient: 'Rokhaya Diop', tel: '+221 78 900 11 22',
    medicament: 'Vitamine C effervescente', adresse: 'Touba Mosquée',
    commune: 'Touba', montant: 1800, paiement: 'Cash',
    statut: 'REFUSEE', livreurId: null, heure: '09:50',
    note: 'Zone trop éloignée',
  },
];

const STATUT = {
  EN_ATTENTE: { label: 'En attente', bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-400' },
  EN_COURS:   { label: 'En cours',   bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-400 animate-pulse' },
  LIVREE:     { label: 'Livrée',     bg: 'bg-emerald-100',text: 'text-emerald-700', dot: 'bg-emerald-500' },
  REFUSEE:    { label: 'Refusée',    bg: 'bg-red-100',    text: 'text-red-700',     dot: 'bg-red-400' },
};

const PAIEMENT_COLOR = {
  Wave: 'bg-blue-50 text-blue-600',
  OM:   'bg-orange-50 text-orange-600',
  Cash: 'bg-gray-100 text-gray-600',
};

export default function LivraisonOperateur() {
  const [commandes, setCommandes] = useState(MOCK_COMMANDES_INIT);
  const [filtreStatut, setFiltreStatut] = useState('TOUS');
  const [selectedCmd, setSelectedCmd] = useState(null);
  const [livreurModal, setLivreurModal] = useState(null); // cmdId en cours d'assignation

  // Stats
  const total     = commandes.length;
  const enAttente = commandes.filter(c => c.statut === 'EN_ATTENTE').length;
  const enCours   = commandes.filter(c => c.statut === 'EN_COURS').length;
  const livrees   = commandes.filter(c => c.statut === 'LIVREE').length;
  const ca        = commandes.filter(c => c.statut === 'LIVREE').reduce((s, c) => s + c.montant, 0);

  const commandesFiltrees = filtreStatut === 'TOUS'
    ? commandes
    : commandes.filter(c => c.statut === filtreStatut);

  // Actions
  const assignerLivreur = (cmdId, livreurId) => {
    setCommandes(prev => prev.map(c =>
      c.id === cmdId
        ? { ...c, statut: 'EN_COURS', livreurId }
        : c
    ));
    setLivreurModal(null);
  };

  const marquerLivree = (cmdId) => {
    setCommandes(prev => prev.map(c =>
      c.id === cmdId ? { ...c, statut: 'LIVREE', note: c.note || `Livré à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` } : c
    ));
    if (selectedCmd?.id === cmdId) setSelectedCmd(prev => ({ ...prev, statut: 'LIVREE' }));
  };

  const refuserCommande = (cmdId) => {
    setCommandes(prev => prev.map(c =>
      c.id === cmdId ? { ...c, statut: 'REFUSEE' } : c
    ));
    if (selectedCmd?.id === cmdId) setSelectedCmd(null);
  };

  const getLivreur = (id) => MOCK_LIVREURS.find(l => l.id === id);

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
            <Truck size={20} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Gestion des Livraisons</h1>
            <p className="text-sm text-gray-400">Vue opérateur — toutes les demandes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <Star size={12} />Données de démonstration
          </span>
          <button
            onClick={() => setCommandes(MOCK_COMMANDES_INIT)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-all"
          >
            <RefreshCw size={14} />Réinitialiser
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total',      value: total,     icon: Package,      color: 'text-gray-600',    bg: 'bg-gray-50'     },
          { label: 'En attente', value: enAttente,  icon: Clock,        color: 'text-orange-600',  bg: 'bg-orange-50'   },
          { label: 'En cours',   value: enCours,    icon: Bike,         color: 'text-blue-600',    bg: 'bg-blue-50'     },
          { label: 'Livrées',    value: livrees,    icon: CheckCircle,  color: 'text-emerald-600', bg: 'bg-emerald-50'  },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className={`w-8 h-8 ${s.bg} rounded-lg flex items-center justify-center mb-3`}>
              <s.icon size={16} className={s.color} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Liste commandes ── */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">

            {/* Filtres */}
            <div className="p-4 border-b border-gray-100 flex gap-2 flex-wrap">
              {['TOUS', 'EN_ATTENTE', 'EN_COURS', 'LIVREE', 'REFUSEE'].map(f => (
                <button key={f}
                  onClick={() => setFiltreStatut(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    filtreStatut === f
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f === 'TOUS' ? `Tous (${total})` : `${STATUT[f]?.label} (${commandes.filter(c => c.statut === f).length})`}
                </button>
              ))}
            </div>

            {/* Liste */}
            <div className="divide-y divide-gray-50">
              {commandesFiltrees.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Package size={32} className="mx-auto mb-2 opacity-25" />
                  <p className="text-sm">Aucune commande dans ce statut</p>
                </div>
              )}
              {commandesFiltrees.map(cmd => {
                const s = STATUT[cmd.statut];
                const livreur = getLivreur(cmd.livreurId);
                const isSelected = selectedCmd?.id === cmd.id;
                return (
                  <div key={cmd.id}
                    onClick={() => setSelectedCmd(isSelected ? null : cmd)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-all ${isSelected ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-gray-400">{cmd.id}</span>
                        <span className="text-xs text-gray-400">· {cmd.heure}</span>
                        {cmd.note?.toLowerCase().includes('urgence') && (
                          <span className="bg-red-100 text-red-600 text-xs font-bold px-1.5 py-0.5 rounded">URGENCE</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                        <span className={`text-xs font-semibold ${s.text}`}>{s.label}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      <User size={13} className="text-gray-400" />
                      <span className="text-sm font-semibold text-gray-900">{cmd.patient}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${PAIEMENT_COLOR[cmd.paiement]}`}>{cmd.paiement}</span>
                    </div>

                    <div className="flex items-center gap-2 mb-1">
                      <Package size={13} className="text-gray-400" />
                      <span className="text-sm text-gray-600">{cmd.medicament}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MapPin size={13} className="text-gray-400" />
                        <span className="text-xs text-gray-500">{cmd.adresse}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-800">{cmd.montant.toLocaleString()} F</span>
                    </div>

                    {livreur && (
                      <div className="flex items-center gap-1.5 mt-2">
                        <Bike size={12} className="text-blue-400" />
                        <span className="text-xs text-blue-600 font-medium">{livreur.nom}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Panneau détail ── */}
        <div className="space-y-4">

          {selectedCmd ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">{selectedCmd.id}</h3>
                <button onClick={() => setSelectedCmd(null)} className="text-gray-400 hover:text-gray-600">✕</button>
              </div>

              <div className="space-y-3 mb-4 text-sm">
                <Row icon={User}    label="Patient"    value={selectedCmd.patient} />
                <Row icon={Phone}   label="Téléphone"  value={selectedCmd.tel} href={`tel:${selectedCmd.tel}`} />
                <Row icon={Package} label="Médicament" value={selectedCmd.medicament} />
                <Row icon={MapPin}  label="Adresse"    value={`${selectedCmd.adresse} (${selectedCmd.commune})`} />
                <Row icon={Clock}   label="Heure"      value={selectedCmd.heure} />
                {selectedCmd.note && <Row icon={AlertCircle} label="Note" value={selectedCmd.note} />}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-2">

                {/* EN_ATTENTE → assigner livreur ou refuser */}
                {selectedCmd.statut === 'EN_ATTENTE' && (
                  <>
                    <button
                      onClick={() => setLivreurModal(selectedCmd.id)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <Bike size={16} />Assigner un livreur
                    </button>
                    <button
                      onClick={() => refuserCommande(selectedCmd.id)}
                      className="w-full bg-red-50 hover:bg-red-100 text-red-600 font-semibold py-2.5 rounded-xl text-sm transition-all"
                    >
                      Refuser la commande
                    </button>
                  </>
                )}

                {/* EN_COURS → marquer livrée */}
                {selectedCmd.statut === 'EN_COURS' && (
                  <>
                    <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                      <Bike size={14} className="text-blue-500" />
                      <span className="text-xs text-blue-700 font-medium">
                        Livreur : {getLivreur(selectedCmd.livreurId)?.nom}
                      </span>
                    </div>
                    <button
                      onClick={() => marquerLivree(selectedCmd.id)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle size={16} />Marquer comme livrée
                    </button>
                  </>
                )}

                {selectedCmd.statut === 'LIVREE' && (
                  <div className="flex items-center justify-center gap-2 p-3 bg-emerald-50 rounded-xl">
                    <CheckCircle size={16} className="text-emerald-600" />
                    <span className="text-sm font-semibold text-emerald-700">Commande livrée ✓</span>
                  </div>
                )}

                {selectedCmd.statut === 'REFUSEE' && (
                  <div className="flex items-center justify-center gap-2 p-3 bg-red-50 rounded-xl">
                    <XCircle size={16} className="text-red-500" />
                    <span className="text-sm font-semibold text-red-600">Commande refusée</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center text-gray-400">
              <Package size={32} className="mx-auto mb-2 opacity-20" />
              <p className="text-sm">Clique sur une commande pour voir les détails</p>
            </div>
          )}

          {/* Livreurs disponibles */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="font-semibold text-gray-900 text-sm mb-3 flex items-center gap-2">
              <Bike size={15} className="text-emerald-600" />Livreurs
            </h3>
            <div className="space-y-2">
              {MOCK_LIVREURS.map(l => (
                <div key={l.id} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{l.nom}</p>
                    <p className="text-xs text-gray-400">{l.tel}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${l.dispo ? 'bg-emerald-400' : 'bg-gray-300'}`} />
                    <span className={`text-xs ${l.dispo ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {l.dispo ? 'Dispo' : 'Occupé'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CA du jour */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-4 text-white">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={15} />
              <span className="text-sm font-medium text-emerald-100">CA livré aujourd'hui</span>
            </div>
            <p className="text-2xl font-bold">{ca.toLocaleString()} <span className="text-sm font-normal">FCFA</span></p>
            <p className="text-xs text-emerald-200 mt-1">{livrees} livraison{livrees > 1 ? 's' : ''} complétée{livrees > 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {/* ── Modal assignation livreur ── */}
      {livreurModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="font-bold text-gray-900 mb-4">Choisir un livreur</h3>
            <div className="space-y-2 mb-4">
              {MOCK_LIVREURS.filter(l => l.dispo).map(l => (
                <button key={l.id}
                  onClick={() => assignerLivreur(livreurModal, l.id)}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50 transition-all"
                >
                  <div className="text-left">
                    <p className="font-semibold text-gray-900 text-sm">{l.nom}</p>
                    <p className="text-xs text-gray-400">{l.tel}</p>
                  </div>
                  <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                </button>
              ))}
            </div>
            <button onClick={() => setLivreurModal(null)}
              className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-sm text-gray-600 font-semibold transition-all">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, href }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-gray-400">{label}</p>
        {href
          ? <a href={href} className="text-sm font-medium text-blue-600 hover:underline">{value}</a>
          : <p className="text-sm font-medium text-gray-800">{value}</p>
        }
      </div>
    </div>
  );
}
